import React from "react";

const formatLabel = (text) => {
    if (!text) return "";
    const str = String(text).trim();
    if (!str) return "";

    const words = str.split(" ");
    if (words.length === 1) {
        return [str];
    }

    const totalLength = str.length;
    const target = totalLength / 2;
    let current = "";
    let splitIndex = words.length;

    for (let i = 0; i < words.length; i++) {
        const word = words[i];
        const test = current ? `${current} ${word}` : word;
        const testLength = test.length;

        if (testLength >= target && current) {
            splitIndex = i;
            break;
        }

        current = test;
    }

    const firstLine = words.slice(0, splitIndex).join(" ");
    const secondLine = words.slice(splitIndex).join(" ");

    return secondLine ? [firstLine, secondLine] : [firstLine];
};

const toNumberOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const str = String(value).trim();
    if (!str) return null;
    const num = Number(str);
    return Number.isNaN(num) ? null : num;
};

const getNiceStep = (rawStep) => {
    if (!Number.isFinite(rawStep) || rawStep <= 0) return 1;
    const magnitude = 10 ** Math.floor(Math.log10(rawStep));
    const normalized = rawStep / magnitude;
    if (normalized <= 1) return 1 * magnitude;
    if (normalized <= 2) return 2 * magnitude;
    if (normalized <= 5) return 5 * magnitude;
    return 10 * magnitude;
};

const buildAxisScale = (maxValue, tickCount = 6) => {
    const safeMax = Number.isFinite(maxValue) && maxValue > 0 ? maxValue : 0;
    const rawStep = safeMax / Math.max(1, tickCount - 1);
    const step = getNiceStep(rawStep || 1);
    const axisMax = Math.max(step, Math.ceil(safeMax / step) * step);
    const ticks = [];
    for (let value = 0; value <= axisMax; value += step) {
        ticks.push(value);
    }
    if (ticks[ticks.length - 1] !== axisMax) {
        ticks.push(axisMax);
    }
    return { axisMax, ticks };
};

export default function MflBusinessInterruptionChart({ rows = [] }) {
    const [isMflVisible, setIsMflVisible] = React.useState(true);

    const filtered = React.useMemo(
        () =>
            (rows || [])
                .filter((row) => {
                    const riskName = (row.risk || "").trim();
                    const weeks = toNumberOrNull(row.mflWeeks);
                    return !!riskName && weeks !== null;
                })
                .map((row) => ({
                    label: formatLabel(row.risk),
                    weeks: Number(toNumberOrNull(row.mflWeeks) ?? 0),
                }))
                .sort((a, b) => {
                    if (b.weeks !== a.weeks) return b.weeks - a.weeks;
                    return a.label.join(" ").localeCompare(b.label.join(" "));
                }),
        [rows]
    );

    const displayItems = React.useMemo(() => {
        const withVisibleValue = filtered.map((item) => ({
            ...item,
            value: isMflVisible ? item.weeks : 0,
        }));

        return withVisibleValue.sort((a, b) => {
            if (b.value !== a.value) return b.value - a.value;
            return a.label.join(" ").localeCompare(b.label.join(" "));
        });
    }, [filtered, isMflVisible]);

    const axisMaxFromData = displayItems.reduce((max, item) => Math.max(max, item.value), 0);
    const { axisMax, ticks } = React.useMemo(
        () => buildAxisScale(axisMaxFromData, 6),
        [axisMaxFromData]
    );

    return (
        <div className="w-full overflow-x-auto">
            <div className="min-w-[900px] rounded bg-white p-6">
                <div className="grid grid-cols-[160px_minmax(0,1fr)_140px] gap-x-6">
                    <div className="space-y-5">
                        {displayItems.map((item) => (
                            <div key={item.label.join("-")} className="min-h-[36px] text-right text-xs font-bold uppercase text-gray-900">
                                {item.label.map((line, index) => (
                                    <div key={`${line}-${index}`} className="leading-tight">
                                        {line}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>

                    <div>
                        <div className="space-y-5">
                            {displayItems.map((item) => {
                                const value = item.value;
                                const barWidth = axisMax > 0 ? Math.max((value / axisMax) * 100, 0) : 0;

                                return (
                                    <div key={`${item.label.join("-")}-bar`} className="relative h-9">
                                        <div
                                            className="h-full rounded-full bg-[#f2c53d] transition-all duration-200"
                                            style={{ width: `${barWidth}%` }}
                                        />
                                    </div>
                                );
                            })}
                        </div>

                        <div className="mt-7 px-1">
                            <div className="flex items-center justify-between text-lg font-medium text-gray-900">
                                {ticks.map((tick) => (
                                    <span key={tick}>{tick}</span>
                                ))}
                            </div>
                            <div className="mt-1 text-center text-xl text-gray-900">Weeks</div>
                        </div>
                    </div>

                    <div className="flex items-center">
                        <button
                            type="button"
                            onClick={() => setIsMflVisible((prev) => !prev)}
                            className={`flex items-center gap-3 text-sm font-bold text-gray-700 transition-opacity ${isMflVisible ? "opacity-100" : "opacity-40"}`}
                        >
                            <span className="h-5 w-8 rounded-full bg-[#f2c53d]" />
                            <span>MFL (Weeks)</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}


